import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./LoginPage.css";
import AuthFooter from "../Shared/AuthFooter/AuthFooter";

import { getGoogleLoginUrl } from "../../Api/Google/Auth";
import { getKakaoLoginUrl } from "../../Api/Kakao/Auth";
import { getNaverLoginUrl } from "../../Api/Naver/Auth";

import { useAuth } from "../../Context/AuthContext";
import { localLogin, resendVerificationEmail } from "../../Api/Backend/Auth";
import { LOGIN_ENABLED } from "../../Api/Config";


const LoginPage = () => {
  const [id, setId] = useState("");
  const [password, setPassword] = useState("");
  const [rememberId, setRememberId] = useState(false);

  const location = useLocation();
  
  const [errorMessage, setErrorMessage] = useState(location.state?.error || null);
  const [isVerificationEmailSent, setIsVerificationEmailSent] = useState(false);
  const [isVerificationError, setIsVerificationError] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const navigate = useNavigate();
  const { login, isLoggedIn } = useAuth();

  const bypassLogin = () => {
    const user = {
      accessToken: null,
      refreshToken: null,
      authorityType: "GUEST",
      email: "LOGIN BYPASS",
    }
    login(user);
    navigate("/", { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!LOGIN_ENABLED) {
      bypassLogin();
      return;
    }

    await localLogin({
      email: id,
      password: password,
      onSuccess: (data) => {
        const user = {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          authorityType: data.authorityType,
          email: data.email,
        }
        login(user);
        navigate("/", { replace: true });
      },
      onError: (error) => {
        console.error("Local login failed:", error);
        setErrorMessage(error.message || "Unexpected login error.");
        if (error?.details?.error === "EMAIL_NOT_VERIFIED") {
          setIsVerificationError(true);
          console.log("verification problem");
        }
      }
    })
  };

  const handleResendEmail = async () => {
    if (!id || !password) {
      setErrorMessage("예상치 못한 오류입니다. 이메일을 다시 보내기에는 정보가 충분하지 않습니다.");
      return;
    }

    setIsSending(true);
    setIsVerificationEmailSent(false);

    await resendVerificationEmail({
      email: id,
      password: password,
      onSuccess: () => {
        setIsVerificationEmailSent(true);
        setIsSending(false);
      },
      onError: (error) => {
        setErrorMessage(error?.message || "이메일을 다시 보내는 중에 오류가 발생했습니다.");
        setIsSending(false);
      }
    });

  };

  const handleGoogleLoginClick = () => {
    if (!LOGIN_ENABLED) {
      bypassLogin();
      return;
    }
    window.location.href = getGoogleLoginUrl();
  };

  const handleKakaoLoginClick = () => {
    if (!LOGIN_ENABLED) {
      bypassLogin();
      return;
    }
    window.location.href = getKakaoLoginUrl();
  };

  const handleNaverLoginClick = () => {
    if (!LOGIN_ENABLED) {
      bypassLogin();
      return;
    }
    window.location.href = getNaverLoginUrl();
  }

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/", { replace: true });
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (location.state?.error) {
      setErrorMessage(location.state.error);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  return (
    <div className="login-page-container">
      <div className="login-form-wrapper">
        <div className="login-logo">
          <img src="/images/logo.svg" alt="에이콘 로고" />
        </div>
        <form onSubmit={handleSubmit} className="login-form">
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="아이디를 입력해 주세요"
            className="login-input"
            required
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="비밀번호를 입력해 주세요"
            className="login-input"
            required
          />
          <div className="remember-id-section">
            <input
              type="checkbox"
              id="rememberId"
              checked={rememberId}
              onChange={(e) => setRememberId(e.target.checked)}
            />
            <label htmlFor="rememberId">아이디 저장</label>
          </div>
          <button
            type="submit"
            className="login-button"
            disabled={isSending}
          >
            로그인
          </button>
        </form>
        <div className="login-helpers">
          <Link to="/login/password-reset-request">비밀번호 찾기</Link>
          <span className="separator-dot">|</span>
          <Link to="/signup">회원가입</Link>
        </div>

        {errorMessage !== null &&
          <div className="login-page-error">{errorMessage}</div>
        }

        {isVerificationError && (
          <>
            {isVerificationEmailSent ? (
              <div className="email-resent">
                이메일이 전송되었습니다. 이메일을 확인하시고 인증을 완료해주세요.
              </div>
            ) : (
              <div
                className="resend-email-button"
                onClick={handleResendEmail}
                disabled={isSending}
              >
                {isSending ? "전송 중..." : "인증 이메일 다시 보내기"}
              </div>
            )}
          </>
        )}

        <div className={`social-login-section ${errorMessage !== null ? "error" : ""}`}>
          <p className="social-login-separator">간편 로그인</p>
          <div className="social-buttons">
            <a href="#" className="social-button" onClick={handleGoogleLoginClick}>
              <img src="/images/google-logo.png" alt="Google" />
              <span>구글</span>
            </a>
            <a href="#" className="social-button">
              <img src="/images/naver-logo.png" alt="Naver" onClick={handleNaverLoginClick} />
              <span>네이버</span>
            </a>
            <a href="#" className="social-button" onClick={handleKakaoLoginClick}>
              <img src="/images/kakao-logo-color.png" alt="Kakao" />
              <span>카카오</span>
            </a>
          </div>
        </div>
      </div>
      <AuthFooter />
    </div>
  );
};

export default LoginPage;
